/** Single source of truth for route paths. Never hardcode paths in components. */
export const ROUTES = {
  ROOT: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  SET_PASSWORD: '/set-password',
  FORCE_PASSWORD_CHANGE: '/set-initial-password',
  DASHBOARD: '/dashboard',
  // Landing for an account whose role grants no module at all — every
  // module, the dashboard included, is a permission now.
  NO_MODULES: '/dashboard/no-access',

  // Platform master modules
  // Tenant onboarding — one entry point that sets up either an organization or
  // a center, each with its own administrator account. The two older
  // single-purpose forms (SUPER_ADMIN_NEW below, CENTER_REGISTER) still exist:
  // super admins use CENTER_REGISTER to add centers inside their own org.
  ONBOARDING_NEW: '/dashboard/onboarding/new',
  SUPER_ADMINS: '/dashboard/super-admins',
  SUPER_ADMIN_NEW: '/dashboard/super-admins/new',
  SUPER_ADMIN_VIEW: (id) => `/dashboard/super-admins/${id}`,

  // Account (from the header user menu)
  PROFILE: '/dashboard/profile',
  SETTINGS: '/dashboard/settings',
  // Settings is tabbed and the tab lives in the URL, so a tab is linkable and
  // survives a refresh. Unknown tabs fall back to Security.
  SETTINGS_TAB: (tab) => `/dashboard/settings/${tab}`,
  CENTER_PROFILE: '/dashboard/center-profile',
  // Per-category in-app/email toggle matrix for this center.
  CENTER_NOTIFICATION_PREFERENCES: '/dashboard/center-notification-preferences',
  // The super admin's counterpart to CENTER_PROFILE — their own
  // organization's name and logo.
  ORGANIZATION_PROFILE: '/dashboard/organization-profile',
  NOTIFICATIONS: '/dashboard/notifications',
  MESSAGES: '/dashboard/messages',

  // To-dos (available to every signed-in user, like Messages)
  TODOS: '/dashboard/todos',
  TODO_INVITATIONS: '/dashboard/todos/invitations',
  TODO_LIST_VIEW: (listId) => `/dashboard/todos/lists/${listId}`,
  TODO_VIEW: (todoId) => `/dashboard/todos/${todoId}`,

  // Collaboration (available to every signed-in user — anyone can be invited).
  // The module is a set of named "Collaborations": a list of collaboration
  // containers, each owning organizations, programs and events.
  COLLABORATIONS: '/dashboard/collaborations',
  // Create a new collaboration (repeatable member rows form).
  COLLABORATION_CREATE: '/dashboard/collaborations/create',
  // One collaboration's detail (Programs / Events tabs).
  COLLABORATION_VIEW: (id) => `/dashboard/collaborations/${id}`,
  // A collaboration's info screen (stats + organizations table).
  COLLABORATION_INFO: (id) => `/dashboard/collaborations/${id}/info`,
  // Edit a collaboration (same form as create, in edit mode).
  COLLABORATION_EDIT: (id) => `/dashboard/collaborations/${id}/edit`,
  // [H] Manage Collaborators for a specific Program/Event inside a collaboration
  // (the assignment-based access screen). `resource` = 'applications' | 'events'.
  COLLABORATION_MANAGE_COLLABORATORS: (id, resource, recordId) =>
    `/dashboard/collaborations/${id}/records/${resource}/${recordId}/collaborators`,
  // Program/Event detail reached from inside a collaboration (carries the
  // collaboration context banner via a `?group=` query param).
  COLLAB_PROGRAM_VIEW: (id) => `/dashboard/collaborations/programs/${id}`,
  COLLAB_EVENT_VIEW: (id) => `/dashboard/collaborations/events/${id}`,
  // One generic page to manage a record's collaborators (invite + list),
  // reused by Programs and Events (the per-record grant flow — unchanged).
  COLLABORATION_MANAGE: (resource, recordId) =>
    `/dashboard/collaborations/manage/${resource}/${recordId}`,

  // Center-admin modules
  REPORTS: '/dashboard/reports',
  REPORT_VIEW: (id) => `/dashboard/reports/${id}`,
  MENTORING: '/dashboard/mentoring',
  MENTORING_VIEW: (id) => `/dashboard/mentoring/${id}`,
  DOCUMENT_VERIFICATION: '/dashboard/document-verification',
  DOCUMENT_VERIFICATION_STARTUP: (id) => `/dashboard/document-verification/${id}`,

  // Startup-founder modules
  OPEN_PROGRAMS: '/dashboard/open-programs',
  OPEN_PROGRAM_VIEW: (id) => `/dashboard/open-programs/${id}`,
  OPEN_PROGRAM_APPLY: (id) => `/dashboard/open-programs/${id}/apply`,
  MY_MENTORS: '/dashboard/my-mentors',
  MY_MENTOR_VIEW: (id) => `/dashboard/my-mentors/${id}`,
  MY_SESSIONS: '/dashboard/my-sessions',
  MY_SESSION_VIEW: (id) => `/dashboard/my-sessions/${id}`,
  MY_FUNDING: '/dashboard/my-funding',
  STARTUP_PROFILE: '/dashboard/startup-profile',
  STARTUP_PROFILE_EDIT: '/dashboard/startup-profile/edit',

  // Step-reviewer panel — applications awaiting the signed-in user's review.
  MY_REVIEWS: '/dashboard/my-reviews',

  // Jury-member modules
  JURY_PROGRAMS: '/dashboard/jury-programs',
  JURY_PROGRAM_APPS: (id) => `/dashboard/jury-programs/${id}/applications`,

  // Mentor modules
  MENTOR_CALENDAR: '/dashboard/mentor-calendar',
  MENTOR_SESSIONS: '/dashboard/mentor-sessions',
  MENTOR_SESSION_NEW: '/dashboard/mentor-sessions/new',
  MENTOR_SESSION_EDIT: (id) => `/dashboard/mentor-sessions/${id}/edit`,
  ASSIGNED_STARTUPS: '/dashboard/assigned-startups',
  ASSIGNED_STARTUP_VIEW: (id) => `/dashboard/assigned-startups/${id}`,

  // Centers (super admin)
  CENTERS: '/dashboard/centers',
  CENTER_REGISTER: '/dashboard/centers/register',
  CENTER_VIEW: (id) => `/dashboard/centers/${id}`,
  CENTER_EDIT: (id) => `/dashboard/centers/${id}/edit`,

  // Startups (super admin)
  STARTUPS: '/dashboard/startups',
  STARTUP_NEW: '/dashboard/startups/new',
  STARTUP_VIEW: (id) => `/dashboard/startups/${id}`,
  STARTUP_EDIT: (id) => `/dashboard/startups/${id}/edit`,

  // Users (super admin — under Roles & Users)
  USERS: '/dashboard/roles',
  USER_NEW: '/dashboard/roles/users/new',
  USER_VIEW: (id) => `/dashboard/roles/users/${id}`,
  USER_EDIT: (id) => `/dashboard/roles/users/${id}/edit`,
  // Per-user permission overrides — Center Team members only, so a center id
  // is always required to resolve which membership is being customized.
  USER_PERMISSIONS: (id, centerId) => `/dashboard/roles/users/${id}/permissions?center_id=${centerId}`,

  // Programs (super admin)
  PROGRAMS: '/dashboard/programs',
  PROGRAM_NEW: '/dashboard/programs/new',
  PROGRAM_VIEW: (id) => `/dashboard/programs/${id}`,
  PROGRAM_EDIT: (id) => `/dashboard/programs/${id}/edit`,
  PROGRAM_APPLICATIONS: (id) => `/dashboard/programs/${id}/applications`,
  PROGRAM_APPLICATION_VIEW: (id, appId) => `/dashboard/programs/${id}/applications/${appId}`,

  // Evaluation Process templates (reusable program workflow)
  EVALUATION_PROCESSES: '/dashboard/evaluation-processes',
  EVALUATION_PROCESS_NEW: '/dashboard/evaluation-processes/new',
  EVALUATION_PROCESS_EDIT: (id) => `/dashboard/evaluation-processes/${id}/edit`,
  // Evaluation matrix — jury scoring templates (center admin)
  EVALUATION_TEMPLATES: '/dashboard/evaluation-matrix',
  EVALUATION_TEMPLATE_NEW: '/dashboard/evaluation-matrix/new',
  EVALUATION_TEMPLATE_VIEW: (id) => `/dashboard/evaluation-matrix/${id}`,
  EVALUATION_TEMPLATE_EDIT: (id) => `/dashboard/evaluation-matrix/${id}/edit`,

  // Fund catalog (super admin)
  FUNDS: '/dashboard/funds',
  FUND_NEW: '/dashboard/funds/new',
  FUND_VIEW: (id) => `/dashboard/funds/${id}`,
  FUND_EDIT: (id) => `/dashboard/funds/${id}/edit`,

  // Stakeholders (super admin)
  STAKEHOLDERS: '/dashboard/stakeholders',
  STAKEHOLDER_NEW: '/dashboard/stakeholders/new',
  STAKEHOLDER_VIEW: (id) => `/dashboard/stakeholders/${id}`,
  STAKEHOLDER_ASSIGN: '/dashboard/stakeholders/assignments',
  STAKEHOLDER_ASSIGN_VIEW: (id) => `/dashboard/stakeholders/assignments/${id}`,

  // Common calendar (super admin) — events AND mentoring sessions on one grid,
  // center by center, with both creatable from it.
  CALENDAR: '/dashboard/calendar',

  // Events (super admin)
  EVENTS: '/dashboard/events',
  EVENT_NEW: '/dashboard/events/new',
  EVENT_VIEW: (id) => `/dashboard/events/${id}`,
  EVENT_EDIT: (id) => `/dashboard/events/${id}/edit`,

  // Speakers (super admin)
  SPEAKERS: '/dashboard/speakers',
  SPEAKER_NEW: '/dashboard/speakers/new',
  SPEAKER_VIEW: (id) => `/dashboard/speakers/${id}`,
  SPEAKER_EDIT: (id) => `/dashboard/speakers/${id}/edit`,
};

export default ROUTES;
